/**
 * Chat Oversight Types
 * Types for the super admin chat oversight feature
 */

// Review status for conversations
export type ReviewStatus = 'pending' | 'reviewed' | 'flagged' | 'escalated';

// Alert types for automated issue detection
export type AlertType = 'long_response_time' | 'high_token_usage' | 'low_source_confidence' | 'no_sources_found';

// Alert severity levels
export type AlertSeverity = 'info' | 'warning' | 'critical';

// Chat category for reviewing conversations
export interface ChatCategory {
  id: string;
  name: string;
  description: string | null;
  color: string;
  is_issue_category: boolean;
  display_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

// Review record for a conversation
export interface ChatReview {
  id: string;
  conversation_id: string;
  review_status: ReviewStatus;
  categories: string[];
  notes: string | null;
  reviewed_by: string | null;
  reviewed_at: string | null;
  created_at: string;
  updated_at: string;
}

// Alert record for automated issue detection
export interface ChatAlert {
  id: string;
  conversation_id: string;
  message_id: string | null;
  alert_type: AlertType;
  severity: AlertSeverity;
  details: {
    response_time_ms?: number;
    total_tokens?: number;
    avg_similarity?: number;
    source_count?: number;
    threshold?: number;
    threshold_ms?: number;
    [key: string]: unknown;
  };
  is_dismissed: boolean;
  dismissed_by: string | null;
  dismissed_at: string | null;
  created_at: string;
  // Joined data
  intelligence_conversations?: {
    id: string;
    title: string | null;
    user_id: string;
    users?: {
      id: string;
      full_name: string | null;
      email: string;
    };
  };
}

// Conversation row in oversight list (from RPC function)
export interface OversightConversation {
  id: string;
  user_id: string;
  user_name: string | null;
  user_email: string | null;
  site_id: string | null;
  site_name: string | null;
  protocol_id: string | null;
  protocol_number: string | null;
  title: string | null;
  message_count: number;
  total_tokens: number;
  review_status: ReviewStatus;
  categories: string[];
  alert_count: number;
  created_at: string;
  updated_at: string;
}

// Full conversation detail for oversight view
export interface OversightConversationDetail {
  id: string;
  user_id: string;
  site_id: string | null;
  protocol_id: string | null;
  title: string | null;
  created_at: string;
  updated_at: string;
  // Joined relations
  users: {
    id: string;
    full_name: string | null;
    email: string;
  } | null;
  sites: {
    id: string;
    name: string;
    code: string;
  } | null;
  protocols: {
    id: string;
    protocol_number: string;
    sponsor_name: string | null;
    study_nickname: string | null;
  } | null;
  // Fetched separately
  messages: OversightMessage[];
  review: ChatReview | null;
  alerts: ChatAlert[];
}

// Resolved source document info attached to assistant messages
export interface MessageSource {
  document_id: string;
  filename: string;
  document_type: string | null;
  page_number: number | null;
}

// Message in oversight view (same as regular message but with more fields visible)
export interface OversightMessage {
  id: string;
  conversation_id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  source_chunks: string[];
  sources?: MessageSource[];
  input_tokens: number | null;
  output_tokens: number | null;
  created_at: string;
}

// Oversight dashboard statistics
export interface OversightStats {
  total_conversations: number;
  pending_reviews: number;
  flagged_conversations: number;
  escalated_conversations: number;
  active_alerts: number;
  total_messages: number;
  avg_tokens_per_conversation: number;
  conversations_today: number;
  conversations_this_week: number;
}

// Pagination info returned from API
export interface OversightPagination {
  page: number;
  per_page: number;
  total: number;
  total_pages: number;
}

// API response for conversation list
export interface OversightConversationsResponse {
  conversations: OversightConversation[];
  pagination: OversightPagination;
}

// Filter options for oversight queries
export interface OversightFilters {
  page?: number;
  per_page?: number;
  site_id?: string;
  user_id?: string;
  protocol_id?: string;
  review_status?: ReviewStatus;
  has_alerts?: boolean;
  search?: string;
  date_from?: string;
  date_to?: string;
}

// Review update request
export interface UpdateReviewRequest {
  review_status?: ReviewStatus;
  categories?: string[];
  notes?: string;
}

// Create category request
export interface CreateCategoryRequest {
  name: string;
  description?: string;
  color?: string;
  is_issue_category?: boolean;
}

// Helper to get display info for alert types
export const ALERT_TYPE_INFO: Record<AlertType, { label: string; description: string }> = {
  long_response_time: {
    label: 'Slow Response',
    description: 'Response took longer than expected',
  },
  high_token_usage: {
    label: 'High Token Usage',
    description: 'Unusually high token consumption',
  },
  low_source_confidence: {
    label: 'Low Confidence',
    description: 'Sources had low similarity scores',
  },
  no_sources_found: {
    label: 'No Sources',
    description: 'No relevant documents found',
  },
};

// Helper to get display info for severity levels
export const SEVERITY_INFO: Record<AlertSeverity, { label: string; color: string; bgColor: string }> = {
  info: {
    label: 'Info',
    color: 'text-blue-700 dark:text-blue-300',
    bgColor: 'bg-blue-100 dark:bg-blue-900/40',
  },
  warning: {
    label: 'Warning',
    color: 'text-amber-700 dark:text-amber-300',
    bgColor: 'bg-amber-100 dark:bg-amber-900/40',
  },
  critical: {
    label: 'Critical',
    color: 'text-red-700 dark:text-red-300',
    bgColor: 'bg-red-100 dark:bg-red-900/40',
  },
};

// Helper to get display info for review statuses
export const REVIEW_STATUS_INFO: Record<ReviewStatus, { label: string; color: string; bgColor: string }> = {
  pending: {
    label: 'Pending',
    color: 'text-gray-700 dark:text-gray-300',
    bgColor: 'bg-gray-100 dark:bg-gray-700',
  },
  reviewed: {
    label: 'Reviewed',
    color: 'text-green-700 dark:text-green-300',
    bgColor: 'bg-green-100 dark:bg-green-900/40',
  },
  flagged: {
    label: 'Flagged',
    color: 'text-amber-700 dark:text-amber-300',
    bgColor: 'bg-amber-100 dark:bg-amber-900/40',
  },
  escalated: {
    label: 'Escalated',
    color: 'text-red-700 dark:text-red-300',
    bgColor: 'bg-red-100 dark:bg-red-900/40',
  },
};
